import { QueryClient, useQuery, useQueryClient } from "react-query";
import { DocumentSnapshot } from "firebase/firestore";
import { firestore } from "@/services/firestore";
import { Shop } from "@/types/v2";
import { useTotalShopsQuery } from "./useTotalShopsQuery";

interface UsePaginatedShopsParams {
  currentPage: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
}

interface PaginatedShopsResult {
  docs: Shop[];
  total: number;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
}

// Store cursor snapshots in a Map for efficient lookups
const cursorCache = new Map<string, DocumentSnapshot>();

export const usePaginatedShops = ({
  currentPage,
  pageSize,
  sortBy = "createdAt",
  sortOrder = "desc",
  enabled = true,
}: UsePaginatedShopsParams) => {
  const queryClient = useQueryClient();

  // Use the total shops query
  const { data: totalShops = 0, isLoading: isTotalLoading } =
    useTotalShopsQuery();

  return useQuery({
    queryKey: ["paginated-shops", currentPage, pageSize, sortBy, sortOrder],
    queryFn: async (): Promise<PaginatedShopsResult> => {
      // Generate cache key for cursor
      const prevPageKey = `paginated-shops-${currentPage - 1}-${pageSize}-${sortBy}-${sortOrder}`;
      const currentPageKey = `paginated-shops-${currentPage}-${pageSize}-${sortBy}-${sortOrder}`;

      // Get cursor from previous page if not first page
      let startAfterDoc: DocumentSnapshot | undefined;
      if (currentPage > 1) {
        startAfterDoc = cursorCache.get(prevPageKey);

        // If cursor not in cache, we need to fetch previous pages first
        if (!startAfterDoc) {
          await buildCursorChain(
            currentPage,
            pageSize,
            sortBy ?? "",
            sortOrder
          );
          startAfterDoc = cursorCache.get(prevPageKey);
        }
      }

      // Fetch current page - no need to request total count from Firestore
      const result = await firestore.paginateDocuments<Shop>(
        "/shops",
        pageSize,
        startAfterDoc,
        undefined, // converter
        sortBy ?? undefined,
        sortOrder
      );

      // Cache the cursor for this page
      if (result.lastVisible) {
        cursorCache.set(currentPageKey, result.lastVisible);
      }

      const totalPages = Math.ceil(totalShops / pageSize);

      return {
        docs: result.docs.map((shop) => ({
          ...shop,
        })) as Shop[],
        total: totalShops, // Use total from useTotalShopsQuery
        hasMore: result.hasMore,
        currentPage,
        totalPages,
      };
    },
    enabled: enabled && !isTotalLoading, // Wait for total to load first
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true,
    // Prefetch next page
    onSuccess: (data) => {
      if (data.hasMore) {
        queryClient.prefetchQuery({
          queryKey: [
            "paginated-shops",
            currentPage + 1,
            pageSize,
            sortBy,
            sortOrder,
          ],
          queryFn: async () => {
            const nextPageKey = `paginated-shops-${currentPage}-${pageSize}-${sortBy}-${sortOrder}`;
            const startAfterDoc = cursorCache.get(nextPageKey);

            if (!startAfterDoc) return null;

            const result = await firestore.paginateDocuments<Shop>(
              "/shops",
              pageSize,
              startAfterDoc,
              undefined,
              sortBy ?? undefined,
              sortOrder
            );

            return {
              docs: result.docs as Shop[],
              total: totalShops, // Use cached total
              hasMore: result.hasMore,
              currentPage: currentPage + 1,
              totalPages: data.totalPages,
            };
          },
          staleTime: 5 * 60 * 1000,
        });
      }
    },
  });
};

// Helper function to build cursor chain for jumping to arbitrary pages
/**
 *
 * @param targetPage
 * @param pageSize
 * @param sortBy
 * @param sortOrder
 */
async function buildCursorChain(
  targetPage: number,
  pageSize: number,
  sortBy: string,
  sortOrder: "asc" | "desc"
) {
  let lastDoc: DocumentSnapshot | undefined;

  for (let page = 1; page < targetPage; page++) {
    const cacheKey = `paginated-shops-${page}-${pageSize}-${sortBy}-${sortOrder}`;

    if (!cursorCache.has(cacheKey)) {
      const result = await firestore.paginateDocuments<Shop>(
        "/shops",
        pageSize,
        lastDoc,
        undefined,
        sortBy,
        sortOrder
      );

      if (result.lastVisible) {
        cursorCache.set(cacheKey, result.lastVisible);
        lastDoc = result.lastVisible;
      }
    } else {
      lastDoc = cursorCache.get(cacheKey);
    }
  }
}

// Invalidate cache when shops are modified
const invalidateShopsCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries(["paginated-shops"]);
  cursorCache.clear(); // Clear cursor cache when data changes
};

// Use this after creating, updating, or deleting shops
const onShopUpdate = async (
  queryClient: QueryClient,
  shopId: string,
  updates: Partial<Shop>
) => {
  // Optimistic update
  queryClient.setQueriesData(
    ["paginated-shops"],
    (oldData: PaginatedShopsResult | undefined) => {
      if (!oldData)
        return {
          docs: [],
          total: 0,
          hasMore: false,
          currentPage: 1,
          totalPages: 1,
        };
      return {
        ...oldData,
        docs: oldData.docs.map((shop) =>
          shop.id === shopId ? { ...shop, ...updates } : shop
        ),
      };
    }
  );

  // Then invalidate to refetch from server
  invalidateShopsCache(queryClient);
};

export const useInvalidateShopsCache = () => {
  const queryClient = useQueryClient();
  return () => invalidateShopsCache(queryClient);
};

export const useOnShopUpdate = () => {
  const queryClient = useQueryClient();
  return (shopId: string, updates: Partial<Shop>) =>
    onShopUpdate(queryClient, shopId, updates);
};
